import React from "react";
import { Timesheet } from "domain/entities/timesheet";
import { useNavigate } from "react-router-dom";
import useLongPress from "presentation/mobile/hooks/useLongPress";
import MobileActionButton from "presentation/mobile/components/MobileActionButton";
import { formatCurrency, formatDatePT } from "presentation/mobile/utils/format.util";
import { TIMESHEET_STATUS_TONES, getStatusStyle } from "presentation/mobile/utils/status-styles.util";

interface TimesheetCardProps {
    timesheet: Timesheet;
    isExpanded: boolean;
    onToggle: () => void;
    hasPendingKm?: boolean;
    onFill?: (timesheet: Timesheet) => void;
}

const MONTHS_PT = [
    "01", "02", "03", "04", "05", "06",
    "07", "08", "09", "10", "11", "12",
];

const TimesheetCard: React.FC<TimesheetCardProps> = ({
    timesheet,
    isExpanded,
    onToggle,
    hasPendingKm = false,
}) => {
    const navigate = useNavigate();
    const { isPressing, handlers } = useLongPress({
        onLongPress: onToggle,
    });

    const isEditableStatus = ["Novo", "Editado", "Rejeitado"].includes(timesheet.status);
    const canFill = isEditableStatus && !hasPendingKm;

    const statusInfo = {
        label: timesheet.status ?? "-",
        ...getStatusStyle(TIMESHEET_STATUS_TONES, timesheet.status),
    };

    const monthFormatted = MONTHS_PT[timesheet.month - 1] ?? "??";
    const period = `${monthFormatted}/${timesheet.year}`;

    return (
        <div
            {...handlers}
            className="border-round-xl p-3 flex flex-column gap-2 cursor-pointer"
            style={{
                backgroundColor: "#FFFFFF",
                border: isExpanded ? "1.5px solid #0084D1" : "1px solid #E2E8F0",
                boxShadow: isExpanded ? "0 4px 14px rgba(0, 132, 209, 0.08)" : "none",
                transform: isPressing ? "scale(0.99)" : "scale(1)",
                transition: "all 0.2s ease",
            }}
        >
            {/* Linha 1: Período com ponto azul se expandido + Badge de Estado + Botão '+' / '✕' */}
            <div className="w-full flex justify-content-between align-items-center">
                <div className="flex align-items-center gap-1 overflow-hidden pr-2">
                    {isExpanded && (
                        <span
                            style={{
                                color: "#0084D1",
                                fontSize: "1.1rem",
                                lineHeight: "1",
                                marginRight: "2px",
                            }}
                        >
                            •
                        </span>
                    )}
                    <span className="font-semibold text-sm text-900 white-space-nowrap overflow-hidden text-overflow-ellipsis">
                        {`Mapa de Horas - ${period}`}
                    </span>
                </div>

                <div className="flex align-items-center gap-2 flex-shrink-0">
                    <span
                        className="badge-squircle"
                        style={{
                            color: statusInfo.color,
                            backgroundColor: statusInfo.bg,
                            border: `1px solid ${statusInfo.border}`,
                        }}
                    >
                        {statusInfo.label}
                    </span>

                    {/* Micro-interação: '+' gira 45º para '✕' */}
                    <button
                        type="button"
                        onClick={(e) => {
                            e.stopPropagation();
                            onToggle();
                        }}
                        className="border-none bg-transparent cursor-pointer flex align-items-center justify-content-center p-0"
                        style={{
                            width: "24px",
                            height: "24px",
                            color: "#94A3B8",
                            outline: "none",
                        }}
                        aria-label={isExpanded ? "Recolher" : "Expandir"}
                    >
                        <i
                            className="pi pi-plus text-xs"
                            style={{
                                transform: isExpanded ? "rotate(45deg)" : "rotate(0deg)",
                                transition: "transform 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
                            }}
                        />
                    </button>
                </div>
            </div>

            {/* Linha 2: Versão */}
            <div className="w-full flex justify-content-between align-items-center">
                <span className="text-xs" style={{ color: "#64748B" }}>
                    Versão: <strong style={{ color: "#0F172A" }}>v{timesheet.version ?? 1}</strong>
                </span>
            </div>

            {/* Secção Expandida: Botão Reutilizável de Ação */}
            {isExpanded && (
                <div
                    className="w-full flex flex-column gap-2 pt-2 mt-1"
                    style={{ borderTop: "1px solid #F1F5F9" }}
                >
                    <div className="w-full flex justify-content-end gap-2 pt-1">
                        <MobileActionButton
                            label={canFill ? "Preencher" : "Consultar"}
                            icon={canFill ? "pi pi-file-edit" : "pi pi-eye"}
                            severity={canFill ? "primary" : "secondary"}
                            onClick={() => navigate(`/mobile/timesheets/${timesheet.id}`)}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

export default TimesheetCard;